// By-name Smart Baseline flavors cache + subscribe/notify (leaf module — imports only the response
// type, so anything can read it without an import cycle).
//
// WHY THE NOTIFY: the cache is populated asynchronously (one /flavors fetch per resolved item name).
// A Handsontable cell renderer paints BEFORE its fetch resolves; without a notification it never
// re-renders when its result lands, so on a cold load it stays on the saved price with no flavor
// link until something remounts it (scrolling, or leaving + re-entering the event). That was the
// "partial / erratic reload" bug. This is the same external-store pattern as nsbResolvedItemBridge:
// a cell subscribes by cacheKey and re-renders the instant its flavors are cached.
//
// The cache is keyed by item name (`byname__<item_name_to_use>`). It IS cleared on event change (and
// on a same-event replace-all upload) via the resetter registered at the bottom of this file —
// otherwise a re-uploaded or next-event item would inherit the previous item's cached flavors/baseline
// (the cross-event bleed bug this PR fixes).
import { NsbFlavorsResponse } from '@api/SmartBaselineFlavorsApi';
import { registerNsbEventResetter } from './nsbEventReset';

const flavorsByKey = new Map<string, NsbFlavorsResponse>();
const subscribersByKey = new Map<string, Set<() => void>>();

export const hasCachedFlavors = (cacheKey: string): boolean => flavorsByKey.has(cacheKey);

// Slim (grid) and full (popup) entries share this cache, so callers need to tell them apart.
// The discriminator is a confidence score, which the slim projection omits — NOT `chart_data`:
// an item can have precomputes and no chart rows, and would then look slim forever.
//
// Asks whether the KEY is present, not whether its value is undefined. Today arkin-api's slim path
// selects an explicit column tuple and the router returns the dicts through a bare JSONResponse, so a
// missing score has no key at all — both checks agree. They stop agreeing the moment anything puts a
// response_model in front of that endpoint: the field would then serialise as null, `!== undefined`
// would call every slim entry full, and the popups would quietly stop fetching.
export const isFullFlavors = (value: NsbFlavorsResponse | null): boolean =>
  Boolean(value?.precomputes) && 'conf_score_last_price' in (value?.precomputes ?? {});

// Snapshot read for useSyncExternalStore — returns the SAME object reference until the entry is
// replaced, so React only re-renders when the flavors actually change.
export const getCachedFlavors = (cacheKey: string | null): NsbFlavorsResponse | null =>
  (cacheKey ? flavorsByKey.get(cacheKey) ?? null : null);

// A slim entry never replaces a full one: the grid's slim fetch can land after a popup has cached
// the full payload, and downgrading would strip charts and scores out from under an open popup.
export const setCachedFlavors = (cacheKey: string, value: NsbFlavorsResponse): void => {
  if (!isFullFlavors(value) && isFullFlavors(flavorsByKey.get(cacheKey) ?? null)) return;

  flavorsByKey.set(cacheKey, value);
  subscribersByKey.get(cacheKey)?.forEach(fn => fn());
};

// Does this key hold a FULL entry? A slim entry — or nothing — returns false, so the popup fetches.
export const hasFullCachedFlavors = (cacheKey: string | null): boolean =>
  isFullFlavors(getCachedFlavors(cacheKey));

export const subscribeFlavorsCache = (cacheKey: string | null, fn: () => void): (() => void) => {
  if (!cacheKey) return () => undefined;
  if (!subscribersByKey.has(cacheKey)) subscribersByKey.set(cacheKey, new Set());
  subscribersByKey.get(cacheKey)!.add(fn);
  return () => { subscribersByKey.get(cacheKey)?.delete(fn); };
};

// Bust the by-name flavors cache. It is event-independent and survives navigation by design, so it is
// cleared on event change (registered below) and on a same-event replace-all upload — otherwise a
// re-uploaded (or next-event) item inherits the previous item's cached flavors / baseline. Every
// subscriber is notified so open cells drop their stale snapshot and re-fetch.
export const resetFlavorsCache = (): void => {
  flavorsByKey.clear();
  subscribersByKey.forEach(subs => subs.forEach(fn => fn()));
};
registerNsbEventResetter(resetFlavorsCache);

// Bust ONE cache entry, as opposed to resetFlavorsCache's full clear. The buyer-initiated Refresh button
// needs exactly this: it must force a fresh price fetch for the specific names its still-auto-priced rows
// resolve to, without touching every other name this event-independent cache is holding for unrelated
// rows/events. A no-op if the key was never cached.
export const evictCachedFlavors = (cacheKey: string): void => {
  if (!flavorsByKey.delete(cacheKey)) return;
  subscribersByKey.get(cacheKey)?.forEach(fn => fn());
};
